import { EventEmitter } from 'events';
import { WhatsAppClient } from '../client/WhatsAppClient';

export interface MigrationConfig {
  from: 'whatsapp-web.js' | 'baileys-v4' | 'baileys-v5' | 'custom';
  to: 'baileys-v6' | 'chatsuite' | 'custom';
  fieldMapping: FieldMigrationMap;
  typeMapping: TypeMigrationMap;
  validators: ValidatorMap;
  transformers: MigrationTransformerMap;
  compatibility: CompatibilityConfig;
}

export interface FieldMigrationMap {
  [originalField: string]: string | FieldMigrationConfig;
}

export interface FieldMigrationConfig {
  target: string;
  transform?: (value: any, context?: any) => any;
  required?: boolean;
  default?: any;
  deprecated?: boolean;
  warningMessage?: string;
}

export interface TypeMigrationMap {
  [originalType: string]: string;
}

export interface ValidatorMap {
  [fieldName: string]: (value: any) => boolean;
}

export interface MigrationTransformerMap {
  [transformerName: string]: (data: any, context?: any) => any;
}

export interface CompatibilityConfig {
  wrapMethods: boolean;
  emitDeprecationWarnings: boolean;
  strictMode: boolean;
  fallbackHandlers: { [method: string]: Function };
}

export interface MigrationResult {
  success: boolean;
  migrated: any;
  warnings: string[];
  errors: string[];
  deprecated: string[];
}

export class MigrationHelper extends EventEmitter {
  private config: Required<MigrationConfig>;
  private deprecationWarnings: Set<string> = new Set();

  constructor(config: MigrationConfig) {
    super();
    
    this.config = {
      ...config,
      validators: config.validators || {},
      transformers: config.transformers || {},
      compatibility: config.compatibility ? {
        wrapMethods: config.compatibility.wrapMethods ?? true,
        emitDeprecationWarnings: config.compatibility.emitDeprecationWarnings ?? true,
        strictMode: config.compatibility.strictMode ?? false,
        fallbackHandlers: config.compatibility.fallbackHandlers ?? {}
      } : {
        wrapMethods: true,
        emitDeprecationWarnings: true,
        strictMode: false,
        fallbackHandlers: {}
      }
    };
  }

  /**
   * Migrate data from old format to new format
   */
  migrateData(data: any, context?: any): MigrationResult {
    const result: MigrationResult = {
      success: true,
      migrated: {},
      warnings: [],
      errors: [],
      deprecated: []
    };

    try {
      result.migrated = this.transformData(data, result, context);
    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : String(error));
    }

    this.emit('migration:completed', result);
    return result;
  }

  /**
   * Transform data according to field mapping
   */
  private transformData(data: any, result: MigrationResult, context?: any): any {
    if (Array.isArray(data)) {
      return data.map(item => this.transformData(item, result, context));
    }

    if (data === null || typeof data !== 'object') {
      return data;
    }

    const transformed: any = {};

    // Apply field mappings
    for (const [originalField, mappingConfig] of Object.entries(this.config.fieldMapping)) {
      const config = typeof mappingConfig === 'string' 
        ? { target: mappingConfig } 
        : mappingConfig;

      const value = this.getNestedValue(data, originalField);
      
      if (value !== undefined) {
        try {
          let transformedValue = config.transform 
            ? config.transform(value, context) 
            : value;

          // Apply validators
          if (this.config.validators[config.target]) {
            if (!this.config.validators[config.target](transformedValue)) {
              result.errors.push(`Validation failed for field: ${config.target}`);
              continue;
            }
          }

          this.setNestedValue(transformed, config.target, transformedValue);

          // Handle deprecation warnings
          if (config.deprecated) {
            const warning = config.warningMessage || `Field '${originalField}' is deprecated`;
            if (!this.deprecationWarnings.has(warning)) {
              this.deprecationWarnings.add(warning);
              result.deprecated.push(warning);
              
              if (this.config.compatibility.emitDeprecationWarnings) {
                this.emit('deprecation:warning', { field: originalField, message: warning });
              }
            }
          }

        } catch (error) {
          result.errors.push(`Failed to transform field '${originalField}': ${error instanceof Error ? error.message : String(error)}`);
        }
      } else if (config.required) {
        if (config.default !== undefined) {
          this.setNestedValue(transformed, config.target, config.default);
        } else {
          result.errors.push(`Required field '${originalField}' is missing`);
        }
      }
    }

    // Apply global transformers
    for (const [transformerName, transformer] of Object.entries(this.config.transformers)) {
      try {
        transformed[transformerName] = transformer(transformed, context);
      } catch (error) {
        result.warnings.push(`Transformer '${transformerName}' failed: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return transformed;
  }

  /**
   * Create compatibility wrapper for old client
   */
  wrapClient(client: WhatsAppClient): any {
    if (!this.config.compatibility.wrapMethods) {
      return client;
    }

    const wrapper = new Proxy(client, {
      get: (target, prop: string) => {
        // Check if method exists in new client
        if (prop in target) {
          return (target as any)[prop];
        }

        // Check for fallback handlers
        if (this.config.compatibility.fallbackHandlers[prop]) {
          return this.config.compatibility.fallbackHandlers[prop];
        }

        // Handle deprecated methods
        const deprecatedMethod = this.getDeprecatedMethodMapping(prop);
        if (deprecatedMethod) {
          this.emitDeprecationWarning('method', prop, deprecatedMethod.message);
          return deprecatedMethod.handler;
        }

        // Strict mode throws errors for unknown methods
        if (this.config.compatibility.strictMode) {
          throw new Error(`Method '${prop}' is not available in the new client`);
        }

        // Return undefined for unknown methods
        return undefined;
      }
    });

    return wrapper;
  }

  /**
   * Get mapping for deprecated methods
   */
  private getDeprecatedMethodMapping(method: string): { handler: Function; message: string } | null {
    const deprecatedMethods: { [key: string]: { newMethod: string; message: string } } = {
      // WhatsApp Web.js compatibility
      'sendMessage': {
        newMethod: 'messages.sendText',
        message: 'sendMessage is deprecated. Use messages.sendText instead.'
      },
      'getChats': {
        newMethod: 'chats.list',
        message: 'getChats is deprecated. Use chats.list instead.'
      },
      'getChatById': {
        newMethod: 'chats.get',
        message: 'getChatById is deprecated. Use chats.get instead.'
      },
      'getContacts': {
        newMethod: 'contacts.list',
        message: 'getContacts is deprecated. Use contacts.list instead.'
      },
      'getContactById': {
        newMethod: 'contacts.get',
        message: 'getContactById is deprecated. Use contacts.get instead.'
      },
      'logout': {
        newMethod: 'sessions.delete',
        message: 'logout is deprecated. Use sessions.delete instead.'
      }
    };

    const mapping = deprecatedMethods[method];
    if (!mapping) {
      return null;
    }

    return {
      message: mapping.message,
      handler: (...args: any[]) => {
        this.emitDeprecationWarning('method', method, mapping.message);
        
        // Try to call the new method
        const [resource, methodName] = mapping.newMethod.split('.');
        const client = this as any; // Type assertion for wrapper context
        
        if (client[resource] && client[resource][methodName]) {
          return client[resource][methodName](...args);
        }
        
        throw new Error(`Migration failed: ${mapping.newMethod} is not available`);
      }
    };
  }

  /**
   * Emit deprecation warning
   */
  private emitDeprecationWarning(type: string, name: string, message: string): void {
    const warningKey = `${type}:${name}`;
    
    if (!this.deprecationWarnings.has(warningKey)) {
      this.deprecationWarnings.add(warningKey);
      
      if (this.config.compatibility.emitDeprecationWarnings) {
        console.warn(`[DEPRECATED] ${message}`);
        this.emit('deprecation:warning', { type, name, message });
      }
    }
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }

  /**
   * Set nested value in object using dot notation
   */
  private setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    
    const target = keys.reduce((current, key) => {
      if (current[key] === undefined) {
        current[key] = {};
      }
      return current[key];
    }, obj);
    
    target[lastKey] = value;
  }

  /**
   * Batch migrate multiple items
   */
  migrateBatch(items: any[], context?: any): MigrationResult[] {
    return items.map(item => this.migrateData(item, context));
  }

  /**
   * Validate migration config
   */
  validateConfig(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check required fields
    if (!this.config.from || !this.config.to) {
      errors.push('Both "from" and "to" formats must be specified');
    }

    // Check field mappings
    for (const [field, mapping] of Object.entries(this.config.fieldMapping)) {
      if (typeof mapping === 'object' && !mapping.target) {
        errors.push(`Field mapping for "${field}" must have a target`);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get migration statistics
   */
  getStats(): {
    deprecationWarnings: string[];
    totalWarnings: number;
    supportedFormats: string[];
  } {
    return {
      deprecationWarnings: Array.from(this.deprecationWarnings),
      totalWarnings: this.deprecationWarnings.size,
      supportedFormats: ['whatsapp-web.js', 'baileys-v4', 'baileys-v5', 'baileys-v6']
    };
  }
}

/**
 * Predefined migration configurations
 */
export const MigrationConfigs = {
  /**
   * WhatsApp Web.js to ChatSuite migration
   */
  whatsappWebJs: {
    from: 'whatsapp-web.js' as const,
    to: 'chatsuite' as const,
    fieldMapping: {
      'id._serialized': 'messageId',
      'body': 'text',
      'timestamp': {
        target: 'timestamp',
        transform: (ts: number) => new Date(ts * 1000)
      },
      'fromMe': 'fromMe',
      'author': 'participant',
      'to': 'chatId',
      'type': {
        target: 'messageType',
        transform: (type: string) => {
          const typeMap: { [key: string]: string } = {
            'chat': 'text',
            'image': 'image',
            'video': 'video',
            'audio': 'audio',
            'document': 'document',
            'sticker': 'sticker',
            'location': 'location'
          };
          return typeMap[type] || 'unknown';
        }
      },
      'hasMedia': {
        target: 'hasMedia',
        deprecated: true,
        warningMessage: 'hasMedia is deprecated. Check messageType instead.'
      }
    },
    typeMapping: {
      'chat': 'text',
      'group': 'group',
      'broadcast': 'broadcast'
    },
    validators: {},
    transformers: {},
    compatibility: {
      wrapMethods: true,
      emitDeprecationWarnings: true,
      strictMode: false,
      fallbackHandlers: {}
    }
  },

  /**
   * Baileys v4 to v6 migration
   */
  baileysV4toV6: {
    from: 'baileys-v4' as const,
    to: 'baileys-v6' as const,
    fieldMapping: {
      'key.id': 'messageId',
      'key.remoteJid': 'chatId',
      'key.fromMe': 'fromMe',
      'key.participant': 'participant',
      'messageTimestamp': {
        target: 'timestamp',
        transform: (ts: number) => new Date(parseInt(ts.toString()) * 1000)
      },
      'pushName': 'senderName',
      'message.conversation': 'text',
      'message.extendedTextMessage.text': 'text'
    },
    typeMapping: {},
    validators: {},
    transformers: {
      messageType: (data: any) => {
        if (data.message?.conversation) return 'text';
        if (data.message?.imageMessage) return 'image';
        if (data.message?.videoMessage) return 'video';
        if (data.message?.audioMessage) return 'audio';
        if (data.message?.documentMessage) return 'document';
        return 'unknown';
      }
    },
    compatibility: {
      wrapMethods: false,
      emitDeprecationWarnings: true,
      strictMode: false,
      fallbackHandlers: {}
    }
  }
};